import { all, call, put, select, spawn, take, takeEvery } from 'redux-saga/effects';

import uiActions from '@elab/core/store/ui/actions';
import cartActions from '@elab/core/store/cart/actions';
import cartSelectors from '@elab/core/store/cart/selectors';
import * as configSelectors from '@elab/core/store/config/selectors';
import cartTypes from '@elab/core/store/cart/types';
import userSelectors from '@elab/core/store/user/selectors';
import dynaFormApi from '@elab/core/utils/dynaForms/api';
import logger from '@elab/core/utils/logger';
import analytics from '@elab/core/utils/analytics';
import { removeAppliedOption, updateAppliedOptions } from '@elab/core/utils/giftWrap/sagaHelpers';
import { featureNames, giftOptionsType } from '@elab/core/utils/constants';

import giftWrapApi from './api';
import selectors from './selectors';
import actions from './actions';
import types from './types';

export function* fetchProductWrapMetadata() {
    try {
        const uri = yield call(dynaFormApi.getFormMetadata, 'giftOptionsForm');
        const data = yield call(dynaFormApi.loadMetadata, uri);
        yield put(actions.setProductWrapMetadata(data));

        const { quantity, code } = yield select(selectors.getGiftOptionData);
        const { configurationInfos = [], giftOptions } = yield select(
            cartSelectors.getEntry,
            code,
        ) || {};

        if (giftOptions.length === 1 && !configurationInfos.length) {
            yield all(
                [...Array(quantity)].map((entry, productIdx) =>
                    call(fetchProductWrapDetails, {
                        payload: {
                            value: giftOptions[0].label,
                            productIdx,
                            code,
                            message: '',
                        },
                    }),
                ),
            );
        }

        if (configurationInfos.length) {
            const wrapperCodes = configurationInfos.filter(
                info => info.configurationLabel === 'giftOptionProductCode',
            );
            const messages = configurationInfos.filter(
                info => info.configurationLabel === 'giftOptionMessage',
            );
            const wrapInfos = wrapperCodes.map((wrCode, idx) => ({
                value: wrCode.configurationValue,
                code,
                productIdx: idx,
                message: messages[idx].configurationValue,
            }));
            yield all(wrapInfos.map(entry => call(fetchProductWrapDetails, { payload: entry })));
        }

        yield put(uiActions.openDialog({ name: 'product-wrap-modal' }));
        yield spawn(analytics.handleGiftWrappingStartEvent);
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* fetchProductWrapDetails({ payload }) {
    const { value: wrapCode, productIdx, code: entryCode, message } = payload;
    const entry = yield select(cartSelectors.getEntry, entryCode) || {};
    const selectedWrapEntries = yield select(selectors.getSelectedWrapEntries);
    const isEnabled =
        !!entry.configurationInfos.length || !!wrapCode || selectedWrapEntries.length > 1;

    const option = entry.giftOptions.find(option => option.code === wrapCode);
    const data = !wrapCode ? {} : option;

    yield put(
        actions.setSelectedProductWrap({
            wrap: { productIdx, entryCode, message, data },
            isEnabled,
        }),
    );
}

export function* setGiftOptionData({ payload }) {
    try {
        const giftWrapConfig = yield select(configSelectors.getGiftWrapConfig);
        const allowSingleOptionSelection = giftWrapConfig.allowSingleOptionSelection;
        const uri = yield call(dynaFormApi.getFormMetadata, 'giftOptionsForm');
        const data = yield call(dynaFormApi.loadMetadata, uri);
        yield put(actions.setProductWrapMetadata(data));

        const {
            code,
            giftOptionCode,
            isCartOption,
            giftOptionMessageEnabled,
            isApplied,
            giftOptions,
        } = payload;
        const { orderInfos = [] } = yield select(cartSelectors.getData);
        const entry = yield select(cartSelectors.getEntry, code);
        const { useEngravingMessages } = yield select(configSelectors.getGiftWrapConfig);
        const { appliedOptions, isCurrentOptionApplied } = yield call(
            updateAppliedOptions,
            giftOptionCode,
            isCartOption
                ? orderInfos
                : useEngravingMessages
                ? entry.configurationInfos.filter(
                      configurationInfo =>
                          configurationInfo.configuratorType === featureNames.giftOptions,
                  )
                : entry.configurationInfos,
            giftOptions,
            allowSingleOptionSelection,
        );

        yield put(actions.setGiftOptionData(payload));

        yield put(
            actions.setAppliedGiftOption({
                appliedOptions,
                isCurrentOptionApplied,
            }),
        );

        if (!giftOptionMessageEnabled) {
            isApplied ? yield put(actions.removeGiftData()) : yield put(actions.saveGiftData());
        } else {
            yield put(uiActions.openDialog({ name: 'product-wrap-modal' }));
        }
    } catch (e) {
        yield spawn(logger.error, e);
    }
}

export function* undoGiftOption() {
    const { giftOptionCode } = yield select(selectors.getGiftOptionData);
    const appliedGiftOptions = yield select(selectors.getAppliedGiftOptions);

    const newGiftOptions = yield call(removeAppliedOption, giftOptionCode, appliedGiftOptions);

    yield put(
        actions.setAppliedGiftOption({
            appliedOptions: newGiftOptions,
            isCurrentOptionApplied: true,
        }),
    );
}

export function* removeGiftOption() {
    yield call(undoGiftOption);
    yield put(actions.saveGiftData({ isGiftWrapRemoved: true }));
}

export function* emptyMessageHandler(appliedGiftOptions) {
    const giftWrapConfig = yield select(configSelectors.getGiftWrapConfig);
    const useGiftOptionMessageMandatory = giftWrapConfig.useGiftOptionMessageMandatory;
    if (useGiftOptionMessageMandatory) {
        const giftOptionData = yield select(selectors.getGiftOptionData);
        const giftOptionMessageEnabled = giftOptionData.giftOptionMessageEnabled;
        const giftOptionMessageMandatory = giftOptionData.giftOptionMessageMandatory;
        const gwCode = giftOptionData.giftOptionCode;
        const isMessageField = appliedGiftOptions.find(el => el.giftOptionCode === gwCode);

        return (
            !giftOptionMessageEnabled ||
            !giftOptionMessageMandatory ||
            !isMessageField ||
            !!isMessageField.message
        );
    }
    const allowEmptyMessage = giftWrapConfig.allowEmptyMessage;
    const gwCode = giftWrapConfig.giftWrapCode;
    const isMessageField = appliedGiftOptions.find(el => el.giftOptionCode === gwCode);

    return allowEmptyMessage || !isMessageField || !!isMessageField.message;
}

export function* saveGiftWrap(action = {}) {
    const isGiftWrapRemoved = action.payload ? action.payload.isGiftWrapRemoved : false;
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(cartSelectors.getCartId);
    const { code, isCartOption, giftOptionMessageEnabled } = yield select(
        selectors.getGiftOptionData,
    );
    const entryNumber = yield select(cartSelectors.getEntryNumber, code);
    const giftWrapEntries = yield select(selectors.getSelectedWrapEntries);
    const appliedGiftOptions = yield select(selectors.getAppliedGiftOptions);
    const isShowError = yield select(selectors.getEmptyMsgFeatureFlag);
    const canContinue = yield call(emptyMessageHandler, appliedGiftOptions);

    if (canContinue) {
        try {
            yield call(giftWrapApi.saveGiftWrapData, {
                userId,
                cartId,
                entryNumber,
                isCartOption,
                giftWrapEntries: giftWrapEntries.length ? giftWrapEntries : appliedGiftOptions,
            });
            yield put(cartActions.loadCart());
            yield take(cartTypes.SET_CART_DATA);
        } catch (e) {
            yield call(logger.error, e);
        }
    } else {
        if (!isShowError) {
            yield call(undoGiftOption);
        }
    }
    if (isShowError && !canContinue) {
        yield put(actions.showEmptyMsgError(!canContinue));
    } else {
        const { triggerGiftOptionEvent } = yield select(configSelectors.getGiftWrapConfig);
        yield spawn(analytics.handleGiftWrappingConfirmEvent);
        if (triggerGiftOptionEvent) {
            yield spawn(
                isGiftWrapRemoved
                    ? analytics.handleGiftOptionAbortEvent
                    : analytics.handleGiftOptionStartEvent,
                isCartOption ? cartId : code,
                giftOptionMessageEnabled
                    ? giftOptionsType.GIFTOPTIONS_MESSAGE
                    : giftOptionsType.GIFTOPTIONS,
            );
        }
        yield put(uiActions.closeDialog());
        yield put(actions.cleanSelectedWrapEntries());
    }
}

export function* onCloseGiftWrap() {
    const selectedWrapEntries = yield select(selectors.getSelectedWrapEntries);
    const appliedGiftOptions = yield select(selectors.getAppliedGiftOptions);
    if (selectedWrapEntries.length > 0 || appliedGiftOptions.length > 0) {
        yield put(actions.cleanSelectedWrapEntries());
    }
}

export default () => [
    takeEvery(types.SELECT_PRODUCT_WRAP, fetchProductWrapDetails),
    takeEvery(types.CHOOSE_PRODUCT_WRAP, fetchProductWrapMetadata),
    takeEvery(types.SET_GIFT_DATA, setGiftOptionData),
    takeEvery(types.SAVE_GIFTOPTIONS, saveGiftWrap),
    takeEvery(types.REMOVE_GIFTOPTION, removeGiftOption),
    takeEvery(types.ON_CLOSE, onCloseGiftWrap),
];
